import { useState } from 'react';
import { CheckCircle, UserCircle, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { useCreateCustomer, useCustomerDuplicateByPhone } from '@/features/customers';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Input } from '@/shared/ui/Input';

interface QuickCustomerModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (customerId: string) => void;
}

export function QuickCustomerModal({
  open,
  onClose,
  onCreated,
}: QuickCustomerModalProps): JSX.Element | null {
  const createCustomer = useCreateCustomer();
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const duplicateQuery = useCustomerDuplicateByPhone(open ? phone : undefined);
  const duplicateCustomer = duplicateQuery.data;

  if (!open) return null;

  const resetAndClose = () => {
    setFullName('');
    setPhone('');
    onClose();
  };

  const handleUseExisting = () => {
    if (!duplicateCustomer) return;
    toast.success('Mevcut müşteri seçildi');
    onCreated?.(duplicateCustomer.id);
    resetAndClose();
  };

  const handleSubmit = async () => {
    if (!fullName.trim()) return;
    try {
      const customer = await createCustomer.mutateAsync({
        fullName: fullName.trim(),
        phone: phone.trim() || null,
        preferredContactChannel: phone.trim() ? 'whatsapp' : null,
      });
      toast.success(
        customer.dedupeAction === 'merged_existing'
          ? 'Mevcut müşteri bulundu ve güncellendi'
          : 'Müşteri eklendi',
      );
      onCreated?.(customer.id);
      resetAndClose();
    } catch {
      toast.error('Müşteri eklenemedi');
    }
  };

  return (
    <div className="fixed inset-0 z-biz-modal bg-black/40 flex items-center justify-center p-biz-card">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div>
            <h2 className="text-biz-xl font-bold biz-heading">Hızlı müşteri ekle</h2>
            <p className="text-biz-sm text-biz-muted-foreground mt-1">
              Kasa ve randevu akışlarında kullanılacak temel müşteri kaydı.
            </p>
          </div>
        </CardHeader>
        <CardBody className="flex flex-col gap-biz-content">
          <Input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Ad Soyad"
            autoFocus
          />
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Telefon"
            inputMode="tel"
          />
          {duplicateQuery.isFetching ? (
            <div className="rounded-biz-card border border-biz-border bg-biz-muted/40 px-biz-content-lg py-biz-content text-biz-sm text-biz-muted-foreground">
              Telefon kontrol ediliyor...
            </div>
          ) : duplicateCustomer ? (
            <div className="rounded-biz-card border border-biz-warning-border bg-biz-warning-subtle px-biz-content-lg py-biz-content-lg flex flex-col gap-biz-content">
              <div className="flex items-start gap-biz-content">
                <WarningCircle size={22} weight="fill" className="text-biz-warning shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="font-semibold biz-heading">Bu telefonla mevcut müşteri var</p>
                  <p className="text-biz-sm text-biz-muted-foreground">
                    Yeni kayıt açılmayacak. Mevcut müşteriyi seçebilir veya girdiğiniz bilgileri mevcut kayda merge edebilirsiniz.
                  </p>
                </div>
              </div>
              <div className="rounded-biz-input bg-biz-surface px-biz-content py-biz-content flex items-center gap-biz-content">
                <UserCircle size={28} className="text-biz-muted-foreground" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{duplicateCustomer.fullName}</p>
                  <p className="text-biz-sm text-biz-muted-foreground truncate">
                    {duplicateCustomer.phone}
                    {duplicateCustomer.email ? ` · ${duplicateCustomer.email}` : ''}
                  </p>
                </div>
              </div>
              <Button
                type="button"
                intent="secondary"
                size="sm"
                onClick={handleUseExisting}
                leftIcon={<CheckCircle size={16} />}
              >
                Mevcut müşteriyi seç
              </Button>
            </div>
          ) : null}
          <div className="flex justify-end gap-biz-content">
            <Button type="button" intent="secondary" onClick={resetAndClose}>
              Vazgeç
            </Button>
            <Button type="button" onClick={handleSubmit} loading={createCustomer.isPending}>
              {duplicateCustomer ? 'Merge et ve seç' : 'Kaydet'}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
